import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout/Layout';
import api from '../services/api';
import Toast from '../components/Toast';
import ExerciseInfoModal from '../components/ExerciseInfoModal';
import { DAYS_ES, DAY_LABELS, CATEGORY_LABELS } from '../constants';
import './Today.css';

function Stopwatch() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  function reset() {
    setRunning(false);
    setElapsed(0);
  }

  const mins = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const secs = String(elapsed % 60).padStart(2, '0');

  return (
    <div className="stopwatch">
      <span className="stopwatch-time">{mins}:{secs}</span>
      <div className="stopwatch-btns">
        <button type="button" className="stopwatch-btn" onClick={() => setRunning(r => !r)} aria-label={running ? 'Pausar' : 'Iniciar'}>
          {running ? '⏸' : '▶'}
        </button>
        <button type="button" className="stopwatch-btn stopwatch-btn-reset" onClick={reset} aria-label="Reiniciar">↺</button>
      </div>
    </div>
  );
}

function RestTimer({ seconds, onComplete }) {
  const [remaining, setRemaining] = useState(seconds);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (seconds <= 0) return;
    setRemaining(seconds);
    intervalRef.current = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) { clearInterval(intervalRef.current); onComplete && onComplete(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [seconds, onComplete]);

  if (remaining <= 0) return null;
  return <div className="rest-timer"><span>⏱ Descanso: {remaining}s</span></div>;
}

export default function Today() {
  const [routine, setRoutine]       = useState([]);
  const [sets, setSets]             = useState({});
  const [loading, setLoading]       = useState(true);
  const [completing, setCompleting] = useState(false);
  const [toast, setToast]           = useState(null);
  const [expanded, setExpanded]     = useState({});
  const [prevSessions, setPrevSessions] = useState({});
  const [restTimer, setRestTimer]   = useState(null);
  const [selectedDay, setSelectedDay] = useState(new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [editingCompleted, setEditingCompleted] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [showAddExPicker, setShowAddExPicker] = useState(false);
  const [addExSearch, setAddExSearch] = useState('');
  const [allExercises, setAllExercises] = useState([]);
  const [addingExercise, setAddingExercise] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');
  const [infoModal, setInfoModal]   = useState(null);
  const handleRestComplete = useCallback(() => setRestTimer(null), []);
  const today = new Date();
  const todayWeekday = today.getDay() === 0 ? 6 : today.getDay() - 1;

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadToday = useCallback(async (dayOverride) => {
    setLoading(true);
    const day = dayOverride !== undefined ? dayOverride : selectedDay;
    try {
      const params = day !== todayWeekday ? { day } : {};
      const res = await api.get('/workouts/today', { params });
      const session = res.data.session || {};
      const exercises = session.exercises || [];
      setRoutine(exercises);
      setSessionCompleted(session.completed || false);

      if (session.completed && day === todayWeekday) {
        setShowCompletedModal(true);
      } else {
        setShowCompletedModal(false);
      }

      const initialSets = {};
      exercises.forEach(ex => {
        const isCardio = ex.exercise?.category === 'cardio';
        const logged = ex.logged_sets || [];
        if (logged.length > 0) {
          initialSets[ex.id] = logged.map((s, i) => ({
            id: i, server_id: s.id,
            weight: s.weight != null ? String(s.weight) : '',
            reps: s.reps != null ? String(s.reps) : '',
            done: s.completed || false,
          }));
        } else {
          initialSets[ex.id] = Array.from({ length: ex.sets || 3 }, (_, i) => ({
            id: i, server_id: null,
            weight: isCardio ? String(ex.reps || '') : '',
            reps: isCardio ? '' : String(ex.reps || ''),
            done: false,
          }));
        }
      });
      setSets(initialSets);
      setExpanded(exercises.reduce((acc, ex) => ({ ...acc, [ex.id]: false }), {}));
      setPrevSessions({});
    } catch (err) {
      if (err.response?.status !== 404) showToast("Error al cargar la rutina");
      setRoutine([]);
      setSessionCompleted(false);
    } finally {
      setLoading(false);
    }
  }, [selectedDay, todayWeekday]);

  useEffect(() => { loadToday(); }, []); // eslint-disable-line

  function changeDay(day) {
    setSelectedDay(day);
    setEditingCompleted(false);
    loadToday(day);
  }

  async function toggleExpand(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    if (!expanded[id] && !prevSessions[id]) {
      const ex = routine.find(r => r.id === id);
      if (!ex) return;
      try {
        const histRes = await api.get(`/exercises/${ex.exercise_id}/history`);
        const history = histRes.data.history || [];
        if (history.length > 0) {
          const last = history[0];
          const prev = { weight: last.weight, reps: last.reps };
          setPrevSessions(p => ({ ...p, [id]: prev }));
          const exInfo = ex.exercise || {};
          const isCardio = exInfo.category === 'cardio';
          const isBw = exInfo.is_bodyweight;
          setSets(p => {
            const currentSets = p[id] || [];
            if (currentSets.some(s => s.weight || s.reps)) return p;
            const filled = currentSets.map((s, i) => ({
              ...s,
              weight: isCardio ? (i === 0 && last.weight ? String(last.weight) : s.weight)
                     : isBw ? ''
                     : (last.weight ? String(last.weight) : s.weight),
              reps: isCardio ? ''
                   : (last.reps ? String(last.reps) : s.reps),
            }));
            return { ...p, [id]: filled };
          });
        }
      } catch {}
    }
  }

  function updateSet(exId, setIdx, field, value) {
    setSets(prev => {
      const exSets = [...(prev[exId] || [])];
      exSets[setIdx] = { ...exSets[setIdx], [field]: value };
      return { ...prev, [exId]: exSets };
    });
  }

  function quickWeight(exId, setIdx, delta) {
    setSets(prev => {
      const exSets = [...(prev[exId] || [])];
      const current = parseFloat(exSets[setIdx].weight) || 0;
      const next = Math.max(0, Math.round((current + delta) * 2) / 2);
      exSets[setIdx] = { ...exSets[setIdx], weight: String(next || '') };
      return { ...prev, [exId]: exSets };
    });
  }

  function addSet(exId) {
    setSets(prev => {
      const exSets = prev[exId] || [];
      return { ...prev, [exId]: [...exSets, { id: exSets.length, weight: '', reps: '', done: false }] };
    });
  }

  function removeSet(exId, setIdx) {
    setSets(prev => {
      const exSets = prev[exId].filter((_, i) => i !== setIdx).map((s, i) => ({ ...s, id: i }));
      return { ...prev, [exId]: exSets };
    });
  }

  function markDone(exId, setIdx, routineEntry) {
    const s = (sets[exId] || [])[setIdx];
    if (!s) return;
    updateSet(exId, setIdx, 'done', !s.done);
    if (!s.done && routineEntry?.rest_seconds > 0) {
      setRestTimer({ exId, rest: routineEntry.rest_seconds });
    }
  }

  function completedSetsCount(exId) {
    return (sets[exId] || []).filter(s => s.done).length;
  }

  function totalSetsCount(exId) {
    return (sets[exId] || []).length;
  }

  function validateSets() {
    for (const ex of routine) {
      const isCardio = ex.exercise?.category === 'cardio';
      const isBw = ex.exercise?.is_bodyweight;
      const exSets = sets[ex.id] || [];
      for (let i = 0; i < exSets.length; i++) {
        const s = exSets[i];
        if (!s.done) continue;
        if (isCardio) {
          const t = parseFloat(s.weight);
          if (isNaN(t) || t <= 0) {
            showToast(`"${ex.exercise?.name}": serie ${i + 1} necesita tiempo válido`);
            return false;
          }
        } else if (isBw) {
          const r = parseInt(s.reps, 10);
          if (isNaN(r) || r <= 0) {
            showToast(`"${ex.exercise?.name}": serie ${i + 1} necesita repeticiones válidas`);
            return false;
          }
        } else {
          const w = parseFloat(s.weight);
          const r = parseInt(s.reps, 10);
          if (!isNaN(w) && w < 0) {
            showToast(`"${ex.exercise?.name}": serie ${i + 1} — el peso no puede ser negativo`);
            return false;
          }
          if (isNaN(r) || r <= 0) {
            showToast(`"${ex.exercise?.name}": serie ${i + 1} necesita repeticiones válidas`);
            return false;
          }
        }
      }
    }
    return true;
  }

  const anyDone = routine.some(ex => completedSetsCount(ex.id) > 0);
  const allDone = routine.length > 0 && routine.every(ex =>
    completedSetsCount(ex.id) === totalSetsCount(ex.id) && totalSetsCount(ex.id) > 0
  );

  function getIncompleteMessage() {
    for (const ex of routine) {
      const exSets = sets[ex.id] || [];
      for (let i = 0; i < exSets.length; i++) {
        if (!exSets[i].done) {
          return `"${ex.exercise?.name}": completa la serie ${i + 1} de ${exSets.length}`;
        }
      }
    }
    return null;
  }

  async function completeWorkout() {
    if (routine.length === 0) { showToast('No hay ejercicios para completar'); return; }
    if (!validateSets()) return;
    if (!allDone) {
      const msg = getIncompleteMessage();
      if (msg) { showToast(msg); return; }
    }
    setCompleting(true);
    let hasNewPr = false;
    try {
      for (const ex of routine) {
        const exIsCardio = ex.exercise?.category === 'cardio';
        const exIsBw = ex.exercise?.is_bodyweight;
        for (let idx = 0; idx < (sets[ex.id] || []).length; idx++) {
          const s = sets[ex.id][idx];
          if (!s.done) continue;
          const payload = {
            exercise_id: ex.exercise_id,
            set_number: idx + 1,
            weight: exIsCardio ? (parseFloat(s.weight) || null) : exIsBw ? null : (parseFloat(s.weight) || null),
            reps: exIsCardio ? null : (parseInt(s.reps, 10) || null),
            completed: true,
          };
          if (!s.server_id) {
            const res = await api.post('/workouts/today/sets', payload);
            if (res.data.is_new_pr) hasNewPr = true;
          } else {
            const res = await api.put(`/workouts/sets/${s.server_id}`, payload);
            if (res.data.is_new_pr) hasNewPr = true;
          }
        }
      }
      await api.put('/workouts/today/complete', { notes: sessionNotes });
      showToast(hasNewPr ? '🏆 ¡Nuevo récord personal!' : '¡Entrenamiento completado!', 'success');
      setSessionCompleted(true);
      setEditingCompleted(false);
      setShowCompletedModal(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al guardar el entrenamiento');
    } finally {
      setCompleting(false);
    }
  }

  async function saveEditedWorkout() {
    if (!validateSets()) return;
    setCompleting(true);
    let hasNewPr = false;
    try {
      for (const ex of routine) {
        const exIsCardio = ex.exercise?.category === 'cardio';
        const exIsBw = ex.exercise?.is_bodyweight;
        for (let idx = 0; idx < (sets[ex.id] || []).length; idx++) {
          const s = sets[ex.id][idx];
          const w = exIsCardio ? (parseFloat(s.weight) || null) : exIsBw ? null : (parseFloat(s.weight) || null);
          const r = exIsCardio ? null : (parseInt(s.reps, 10) || null);
          if (!s.server_id && s.done) {
            const res = await api.post('/workouts/today/sets', {
              exercise_id: ex.exercise_id, set_number: idx + 1,
              weight: w, reps: r, completed: true,
            });
            if (res.data.is_new_pr) hasNewPr = true;
          } else if (s.server_id) {
            const res = await api.put(`/workouts/sets/${s.server_id}`, {
              weight: w, reps: r, completed: s.done,
            });
            if (res.data.is_new_pr) hasNewPr = true;
          }
        }
      }
      showToast(hasNewPr ? '🏆 ¡Nuevo récord personal!' : '¡Entrenamiento actualizado!', 'success');
      setEditingCompleted(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al guardar cambios');
    } finally {
      setCompleting(false);
    }
  }

  async function loadAllExercises() {
    try {
      const res = await api.get('/exercises', { params: { per_page: 2000 } });
      setAllExercises(res.data.exercises || []);
    } catch {}
  }

  async function handleAddExternalExercise(ex) {
    setAddingExercise(true);
    try {
      const isCardio = ex.category === 'cardio';
      const res = await api.post('/workouts/today/add-exercise', {
        exercise_id: ex.id,
        sets: isCardio ? 1 : 3,
        reps: isCardio ? '20' : '10',
        rest_seconds: isCardio ? 0 : 60,
      });
      const entry = res.data.routine_exercise;
      const logged = entry.logged_sets || [];
      const newEx = {
        id: entry.id,
        exercise_id: entry.exercise_id,
        exercise: ex,
        sets: isCardio ? 1 : 3,
        reps: isCardio ? '20' : '10',
        rest_seconds: isCardio ? 0 : 60,
        logged_sets: logged,
      };
      setRoutine(prev => [...prev, newEx]);
      const targetReps = isCardio ? '20' : '10';
      setSets(prev => ({
        ...prev,
        [entry.id]: logged.length > 0
          ? logged.map((s, i) => ({
              id: i, server_id: s.id,
              weight: s.weight != null ? String(s.weight) : '',
              reps: s.reps != null ? String(s.reps) : (isCardio ? '' : targetReps),
              done: s.completed || false,
            }))
          : Array.from({ length: isCardio ? 1 : 3 }, (_, i) => ({
              id: i, server_id: null,
              weight: '',
              reps: isCardio ? '' : targetReps,
              done: false,
            })),
      }));
      setExpanded(prev => ({ ...prev, [entry.id]: true }));
      setShowAddExPicker(false);
      setAddExSearch('');
      showToast('¡Ejercicio añadido!', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al añadir ejercicio');
    } finally {
      setAddingExercise(false);
    }
  }

  const dateStr = today.toLocaleDateString('es-ES', { weekday: 'long', month: 'long', day: 'numeric' });
  const isToday = selectedDay === todayWeekday;

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      {restTimer && <RestTimer seconds={restTimer.rest} onComplete={handleRestComplete} />}

      {showCompletedModal && sessionCompleted && (
        <div className="modal-overlay" onClick={() => setShowCompletedModal(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <div className="modal-title" style={{ textAlign: 'center' }}>¡Ya has completado tu entrenamiento de hoy!</div>
            <p className="text-muted text-sm" style={{ textAlign: 'center', marginBottom: 16 }}>
              Puedes editarlo si necesitas hacer cambios.
            </p>
            <button className="btn btn-primary btn-full" onClick={() => { setShowCompletedModal(false); setEditingCompleted(true); }}>
              ✏️ Editar entrenamiento
            </button>
            <button className="btn btn-secondary btn-full mt-8" onClick={() => setShowCompletedModal(false)}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      <div className="page">
        <div className="page-header">
          <div>
            <div className="page-title">Hoy</div>
            <div className="today-date">{dateStr}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Stopwatch />
            <button className="btn btn-ghost btn-sm" onClick={() => loadToday()} aria-label="Actualizar">↺</button>
          </div>
        </div>

        <div className="day-tabs" style={{ paddingTop: 8 }}>
          {DAY_LABELS.map((label, i) => (
            <button
              key={label}
              className={'day-tab' + (selectedDay === i ? ' active' : '') + (i === todayWeekday ? '' : '')}
              onClick={() => changeDay(i)}
              type="button"
            >
              <span className="day-tab-name">{label}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="px-16" style={{ paddingTop: 12 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton skeleton-card" />
            ))}
          </div>
        ) : routine.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🛋️</div>
            <div className="empty-state-title">¡Día de descanso!</div>
            <div className="empty-state-text">
              No hay entrenamiento programado para {isToday ? 'hoy' : `el ${DAYS_ES[selectedDay]}`}.<br />Disfruta tu tiempo de recuperación.
            </div>
          </div>
        ) : (
          <div className="today-content px-16">
            {routine.map((ex) => {
              const done  = completedSetsCount(ex.id);
              const total = totalSetsCount(ex.id);
              const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
              const isExp = expanded[ex.id];
              const prevS = prevSessions[ex.id];

              return (
                <div key={ex.id} className="exercise-card card">
                  <div className="exercise-card-header" onClick={() => toggleExpand(ex.id)}>
                    <div className="exercise-card-info">
                      <span className={`badge badge-${(ex.exercise?.category || 'other').toLowerCase()}`}>
                        {CATEGORY_LABELS[(ex.exercise?.category || 'other').toLowerCase()] || ex.exercise?.category || 'Otro'}
                      </span>
                      <h3 className="exercise-card-name">{ex.exercise?.name}</h3>
                      <div className="exercise-card-meta text-muted text-sm">
                        {ex.exercise?.category === 'cardio' ? (
                          <>Objetivo: {ex.reps} min</>
                        ) : (
                          <>Objetivo: {ex.sets} series × {ex.reps} repeticiones</>
                        )}
                        {ex.rest_seconds ? ` · ${ex.rest_seconds}s descanso` : ''}
                        {prevS && (ex.exercise?.category === 'cardio' ? (
                          <> · <span style={{ color: '#4da6ff' }}>Anterior: {prevS.weight || prevS.reps} min</span></>
                        ) : ex.exercise?.is_bodyweight ? (
                          <> · <span style={{ color: '#4da6ff' }}>Anterior: {prevS.reps} reps</span></>
                        ) : (
                          <> · <span style={{ color: '#4da6ff' }}>Anterior: {prevS.weight}kg × {prevS.reps}</span></>
                        ))}
                      </div>
                    </div>
                    <div className="exercise-card-right">
                      {ex.exercise?.gif_url && (
                        <button
                          type="button"
                          className="exercise-info-btn"
                          onClick={e => { e.stopPropagation(); setInfoModal(ex.exercise); }}
                          aria-label="Información del ejercicio"
                        >
                          ℹ
                        </button>
                      )}
                      <div className="exercise-card-progress">
                        <div className="exercise-card-count">{done}/{total}</div>
                        <div className="exercise-card-chevron">{isExp ? '▲' : '▼'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="progress-bar-track" style={{ margin: '8px 0' }}>
                    <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                  </div>

                  {isExp && (
                    <div className="sets-section">
                      <div className="sets-header">
                        <span className="text-xs text-muted">SERIE</span>
                          {ex.exercise?.category === 'cardio' ? (
                          <span className="text-xs text-muted">TIEMPO (min)</span>
                        ) : ex.exercise?.is_bodyweight ? (
                          <span className="text-xs text-muted">REPS</span>
                        ) : (
                          <>
                            <span className="text-xs text-muted">PESO (kg)</span>
                            <span className="text-xs text-muted">REPS</span>
                          </>
                        )}
                        <span className="text-xs text-muted">HECHO</span>
                        <span />
                      </div>
                      {(sets[ex.id] || []).map((s, idx) => (
                        <div key={s.id} className={'set-row' + (s.done ? ' set-done' : '')}>
                          <span className="set-number">{idx + 1}</span>
                        {ex.exercise?.category === 'cardio' ? (
                            <input className="form-input form-input-sm set-input" type="number" min="0" step="0.5" placeholder="0" value={s.weight} onChange={e => updateSet(ex.id, idx, 'weight', e.target.value)} style={{ gridColumn: '2 / 4' }} />
                          ) : ex.exercise?.is_bodyweight ? (
                            <input className="form-input form-input-sm set-input" type="number" min="0" placeholder="0" value={s.reps} onChange={e => updateSet(ex.id, idx, 'reps', e.target.value)} style={{ gridColumn: '2 / 4' }} />
                          ) : (
                            <>
                              <div className="set-weight-input">
                                <button type="button" className="set-weight-btn" onClick={() => quickWeight(ex.id, idx, -2.5)} tabIndex={-1}>−</button>
                                <input className="form-input form-input-sm set-input" type="number" min="0" step="0.5" placeholder={prevS && prevS.weight != null ? String(prevS.weight) : '0'} value={s.weight} onChange={e => updateSet(ex.id, idx, 'weight', e.target.value)} />
                                <button type="button" className="set-weight-btn" onClick={() => quickWeight(ex.id, idx, 2.5)} tabIndex={-1}>+</button>
                              </div>
                              <input className="form-input form-input-sm set-input" type="number" min="0" placeholder={prevS && prevS.reps != null ? String(prevS.reps) : '0'} value={s.reps} onChange={e => updateSet(ex.id, idx, 'reps', e.target.value)} />
                            </>
                          )}
                          <button type="button" className={'set-done-btn' + (s.done ? ' checked' : '')} onClick={() => markDone(ex.id, idx, ex)} aria-label="Marcar hecho">✓</button>
                          <button type="button" className="set-remove-btn" onClick={() => removeSet(ex.id, idx)} aria-label="Eliminar serie">×</button>
                        </div>
                      ))}
                      {ex.exercise?.category !== 'cardio' && (
                        <button type="button" className="btn btn-secondary btn-sm mt-8" onClick={() => addSet(ex.id)} style={{ width: '100%' }}>
                          + Agregar serie
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              className="btn btn-secondary btn-full mt-16"
              onClick={() => { loadAllExercises(); setShowAddExPicker(true); }}
            >
              + Agregar ejercicio
            </button>

            <div className="today-actions">
              {sessionCompleted ? (
                <>
                  {editingCompleted ? (
                    <button className="btn btn-primary btn-full" onClick={saveEditedWorkout} disabled={completing}>
                      {completing ? <span className="spinner spinner-sm" /> : '💾 Guardar cambios'}
                    </button>
                  ) : (
                    <button className="btn btn-secondary btn-full" onClick={() => setEditingCompleted(true)}>
                      ✏️ Editar entrenamiento
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <textarea
                      className="form-input"
                      placeholder="Notas del entrenamiento (opcional)..."
                      rows={2}
                      value={sessionNotes}
                      onChange={e => setSessionNotes(e.target.value)}
                      style={{ resize: 'none', fontSize: '0.8125rem' }}
                    />
                  </div>
                  <button className="btn btn-primary btn-full" onClick={completeWorkout} disabled={completing}>
                    {completing ? <span className="spinner spinner-sm" /> : 'Completar entrenamiento'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <ExerciseInfoModal exercise={infoModal} onClose={() => setInfoModal(null)} />

      {showAddExPicker && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddExPicker(false)}>
          <div className="modal-sheet">
            <div className="modal-handle" />
            <div className="modal-title">Agregar ejercicio</div>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <span className="search-icon" style={{ left: 12 }}>🔍</span>
              <input className="search-input" style={{ paddingLeft: 36 }} placeholder="Buscar..." value={addExSearch} onChange={e => setAddExSearch(e.target.value)} />
            </div>
            <div className="picker-list">
              {allExercises
                .filter(ex => !routine.some(re => re.exercise_id === ex.id))
                .filter(ex => !addExSearch || ex.name?.toLowerCase().includes(addExSearch.toLowerCase()))
                .map(ex => (
                  <div key={ex.id} className="picker-item" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button
                      type="button"
                      style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', flexWrap: 'wrap' }}
                      onClick={() => handleAddExternalExercise(ex)}
                      disabled={addingExercise}
                    >
                      <span className={`badge badge-${(ex.category || 'other').toLowerCase()}`}>
                        {CATEGORY_LABELS[(ex.category || 'other').toLowerCase()] || ex.category || 'Otro'}
                      </span>
                      <span className="picker-item-name">{ex.name}</span>
                      {ex.is_bodyweight && <span className="badge badge-admin" style={{ fontSize: '0.6rem', padding: '1px 5px' }}>Sin peso</span>}
                    </button>
                    <button
                      type="button"
                      onClick={() => setInfoModal(ex)}
                      style={{ flexShrink: 0, background: 'none', border: '1px solid #2a2a4a', borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0a0b0', cursor: 'pointer', fontSize: '0.8rem' }}
                      aria-label="Ver información"
                    >
                      i
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}
